/* eslint-disable max-lines */
import { Button, Chip } from '@heroui/react';
import { Check, Edit2, ExternalLink, Globe, Mail, MapPin, Phone, X } from 'lucide-react';
import React, { FC, useMemo, useState } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { useNavigate, useParams } from 'react-router-dom';

import {
  ActionBlock,
  ContentLoadError,
  GoogleMapComponent,
  PageTitle,
  PageWrapper,
  Spinner,
  TranslationSwitcher,
} from '../../../components';
import { countryFlagHelper } from '../../../config';
import { useGetAttractionDocumentFromFirestore, useQueryHelper } from '../../../queries';
import { useAuthState, useLangState } from '../../../state';
import { TranslationType } from '../../../types';

const ATTRACTION_TYPE_LABELS: Record<string, string> = {
  FORKIDS: 'For Kids',
  TRIP: 'Trip',
  CULTURE: 'Culture',
  FOODS: 'Food',
};

/** Render plain text preserving paragraph breaks (\n\n) and line breaks. */
const Paragraphs: FC<{ text: string | undefined; placeholder?: string }> = ({
  text,
  placeholder = 'No translation detected',
}) => {
  if (!text || !text.trim()) {
    return <p className="text-sm text-red-500">{placeholder}</p>;
  }
  const blocks = text
    .split(/\n{2,}/)
    .map((b) => b.trim())
    .filter(Boolean);
  return (
    <div className="flex flex-col gap-3 text-sm text-gray-700 leading-relaxed">
      {blocks.map((block, idx) => (
        // eslint-disable-next-line react/no-array-index-key
        <p key={idx} className="whitespace-pre-line">{block}</p>
      ))}
    </div>
  );
};

const hasTranslation = (translation: TranslationType | undefined, lang: string): boolean => {
  const value = translation?.[lang];
  return typeof value === 'string' && value.trim().length > 0;
};

const LangFlag: FC<{ lang: string; size?: number }> = ({ lang, size = 16 }) => {
  const flag = countryFlagHelper(lang);
  if (flag.type === 'iso') {
    return (
      <ReactCountryFlag
        countryCode={lang === 'en' ? 'gb' : lang}
        svg
        style={{ width: size, height: size * 0.75, borderRadius: 2 }}
      />
    );
  }
  return <img src={flag.value} alt={lang} style={{ width: size, height: size * 0.75, borderRadius: 2 }} />;
};

type FieldStatus = { key: string; label: string; present: boolean };

type LangStatus = {
  lang: string;
  fields: FieldStatus[];
  completeCount: number;
  totalCount: number;
};

export const Attraction: FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { defaultLang } = useAuthState();
  const { languages: dbLanguages } = useLangState();
  const attractionDocument = useGetAttractionDocumentFromFirestore(id);
  const { data: attraction, refetch } = attractionDocument;
  const { isLoading, isError, errorMessage } = useQueryHelper({
    queries: [attractionDocument],
  });

  const [selectedLang, setSelectedLang] = useState<string>(defaultLang);

  const attractionLanguages = useMemo<string[]>(() => {
    if (attraction?.languages?.length) return attraction.languages;
    return dbLanguages;
  }, [attraction?.languages, dbLanguages]);

  const showDiscountFields = Boolean(attraction?.showDiscount && attraction?.discount);

  const langStatuses = useMemo<LangStatus[]>(() => {
    if (!attraction) return [];
    return attractionLanguages.map((lang) => {
      const fields: FieldStatus[] = [
        { key: 'title', label: 'Title', present: hasTranslation(attraction.title, lang) },
        { key: 'description', label: 'Description', present: hasTranslation(attraction.description, lang) },
      ];
      if (showDiscountFields) {
        fields.push({ key: 'discountTitle', label: 'Discount title', present: hasTranslation(attraction.discount?.title, lang) });
        fields.push({ key: 'discountDescription', label: 'Discount description', present: hasTranslation(attraction.discount?.description, lang) });
      }
      const completeCount = fields.filter((f) => f.present).length;
      return { lang, fields, completeCount, totalCount: fields.length };
    });
  }, [attraction, attractionLanguages, showDiscountFields]);

  const overallTranslationStats = useMemo(() => {
    if (langStatuses.length === 0) return { complete: 0, total: 0, fullyTranslatedLangs: 0 };
    const total = langStatuses.reduce((acc, l) => acc + l.totalCount, 0);
    const complete = langStatuses.reduce((acc, l) => acc + l.completeCount, 0);
    const fullyTranslatedLangs = langStatuses.filter((l) => l.completeCount === l.totalCount).length;
    return { complete, total, fullyTranslatedLangs };
  }, [langStatuses]);

  if (isLoading) {
    return <Spinner global />;
  }

  if (isError || !attraction) {
    return (
      <PageWrapper>
        <ContentLoadError onRefreshClicked={() => { refetch(); }} message={errorMessage} />
      </PageWrapper>
    );
  }

  const titleInLang = attraction.title?.[selectedLang] || attraction.title?.[defaultLang] || '';
  const descriptionInLang = attraction.description?.[selectedLang];
  const typeLabel = attraction.type ? ATTRACTION_TYPE_LABELS[attraction.type] ?? attraction.type : '';

  return (
    <PageWrapper>
      <PageTitle
        title={titleInLang || 'Attraction'}
        actions={(
          <Button
            variant="solid"
            size="sm"
            color="primary"
            startContent={<Edit2 size={14} color="#fff" />}
            onPress={() => navigate(`/attractions/${id}/edit`)}
          >
            Edit
          </Button>
        )}
      />

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        <div className="xl:col-span-8 flex flex-col gap-4">
          <ActionBlock>
            <div className="flex flex-col md:flex-row gap-5">
              <div className="md:w-[320px] flex-shrink-0">
                <div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden bg-gray-100">
                  {attraction.cover_photo ? (
                    <img
                      alt={titleInLang}
                      src={attraction.cover_photo}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
                      No cover photo
                    </div>
                  )}
                  {attraction.brand_icon && attraction.brand_icon !== attraction.cover_photo && (
                    <div className="absolute bottom-2 left-2 w-12 h-12 rounded-md bg-white border border-gray-200 p-1 shadow-sm flex items-center justify-center">
                      <img
                        src={attraction.brand_icon}
                        alt="Brand"
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="flex-1 flex flex-col gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  {attraction.status && (
                    <Chip
                      size="sm"
                      color={attraction.status === 'LIVE' ? 'success' : 'warning'}
                      variant="flat"
                    >
                      {attraction.status}
                    </Chip>
                  )}
                  {typeLabel && (
                    <Chip size="sm" variant="flat">
                      {typeLabel}
                    </Chip>
                  )}
                  {attraction.recomended && (
                    <Chip size="sm" color="primary" variant="flat">
                      Recommended
                    </Chip>
                  )}
                  {attraction.showDiscount && (
                    <Chip size="sm" color="secondary" variant="flat">
                      Discount
                    </Chip>
                  )}
                </div>

                <h2 className="text-xl font-semibold text-gray-900">{titleInLang || '—'}</h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm text-gray-600">
                  <div className="flex items-start gap-2">
                    <MapPin size={14} className="text-gray-400 mt-0.5 flex-shrink-0" />
                    <span>{attraction.address || attraction.city || '—'}</span>
                  </div>
                  {attraction.web_url && (
                    <div className="flex items-center gap-2">
                      <Globe size={14} className="text-gray-400 flex-shrink-0" />
                      <a
                        href={attraction.web_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary hover:underline truncate inline-flex items-center gap-1"
                      >
                        {attraction.web_url}
                        <ExternalLink size={11} />
                      </a>
                    </div>
                  )}
                  {attraction.email && (
                    <div className="flex items-center gap-2">
                      <Mail size={14} className="text-gray-400 flex-shrink-0" />
                      <a href={`mailto:${attraction.email}`} className="text-primary hover:underline truncate">
                        {attraction.email}
                      </a>
                    </div>
                  )}
                  {attraction.phone && (
                    <div className="flex items-center gap-2">
                      <Phone size={14} className="text-gray-400 flex-shrink-0" />
                      <a href={`tel:${attraction.phone}`} className="text-primary hover:underline">
                        {attraction.phone}
                      </a>
                    </div>
                  )}
                  {attraction.localization?.latitude !== undefined && attraction.localization?.longitude !== undefined && (
                    <div className="flex items-center gap-2 col-span-1 sm:col-span-2 text-xs text-gray-400">
                      <span>
                        {attraction.localization.latitude.toFixed(6)}
                        {', '}
                        {attraction.localization.longitude.toFixed(6)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </ActionBlock>

          <ActionBlock
            title="Description"
            action={(
              <TranslationSwitcher
                languages={attractionLanguages}
                currentLang={selectedLang}
                onSelectedLangChange={(lang) => setSelectedLang(lang)}
              />
            )}
          >
            <div className="mt-3">
              <Paragraphs text={descriptionInLang} />
            </div>
          </ActionBlock>

          {showDiscountFields && (
            <ActionBlock title="Discount">
              <div className="flex flex-col gap-2 mt-2">
                <div className="flex flex-wrap items-center gap-2">
                  {attraction.discount?.discount_code && (
                    <Chip size="sm" variant="flat" color="secondary">
                      Code:&nbsp;
                      <span className="font-mono">{attraction.discount.discount_code}</span>
                    </Chip>
                  )}
                  {typeof attraction.discount?.value === 'number' && attraction.discount.value !== null && (
                    <Chip size="sm" variant="flat" color="warning">
                      {attraction.discount.value}
                      %
                    </Chip>
                  )}
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Title</div>
                  <Paragraphs text={attraction.discount?.title?.[selectedLang]} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Description</div>
                  <Paragraphs text={attraction.discount?.description?.[selectedLang]} />
                </div>
              </div>
            </ActionBlock>
          )}

          <ActionBlock title="Gallery">
            {attraction.gallery && attraction.gallery.length > 0 ? (
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {attraction.gallery.map((image) => (
                  <div key={image} className="aspect-square overflow-hidden rounded-lg bg-gray-100">
                    <img
                      alt="Gallery"
                      src={image}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-3 text-sm text-gray-400 italic">No gallery images uploaded.</div>
            )}
          </ActionBlock>

          <ActionBlock title="Location">
            <div className="mt-3">
              <GoogleMapComponent address={attraction.addressData} showMap />
            </div>
          </ActionBlock>
        </div>

        <div className="xl:col-span-4 flex flex-col gap-4">
          <ActionBlock
            title="Translations"
            description={(
              <span>
                {overallTranslationStats.fullyTranslatedLangs}
                {' / '}
                {langStatuses.length}
                {' languages fully translated · '}
                {overallTranslationStats.complete}
                {' / '}
                {overallTranslationStats.total}
                {' fields'}
              </span>
            )}
          >
            <div className="mt-3 flex flex-col gap-2">
              {langStatuses.length === 0 && (
                <div className="text-sm text-gray-400 italic">
                  No languages assigned to this attraction.
                </div>
              )}
              {langStatuses.map((status) => {
                const isFull = status.completeCount === status.totalCount;
                const isEmpty = status.completeCount === 0;
                const isActive = status.lang === selectedLang;
                let badgeColor: 'success' | 'warning' | 'danger' = 'success';
                if (isEmpty) badgeColor = 'danger';
                else if (!isFull) badgeColor = 'warning';

                return (
                  <button
                    type="button"
                    key={status.lang}
                    onClick={() => setSelectedLang(status.lang)}
                    className={`text-left rounded-lg border p-3 transition-colors ${
                      isActive
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <LangFlag lang={status.lang} />
                        <span className="text-sm font-medium uppercase">{status.lang}</span>
                      </div>
                      <Chip size="sm" color={badgeColor} variant="flat">
                        {status.completeCount}
                        {' / '}
                        {status.totalCount}
                      </Chip>
                    </div>
                    <ul className="flex flex-col gap-1">
                      {status.fields.map((field) => (
                        <li
                          key={field.key}
                          className={`flex items-center gap-2 text-xs ${
                            field.present ? 'text-gray-600' : 'text-red-600'
                          }`}
                        >
                          {field.present ? (
                            <Check size={12} className="text-green-600 flex-shrink-0" />
                          ) : (
                            <X size={12} className="text-red-600 flex-shrink-0" />
                          )}
                          <span>{field.label}</span>
                          {!field.present && (
                            <span className="ml-auto text-red-500 text-[10px] uppercase tracking-wide">
                              missing
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </button>
                );
              })}
            </div>
          </ActionBlock>

          <ActionBlock title="Details">
            <dl className="mt-2 flex flex-col gap-2 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-gray-500">Type</dt>
                <dd className="text-gray-900 font-medium">{typeLabel || '—'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-gray-500">City</dt>
                <dd className="text-gray-900 font-medium">{attraction.city || '—'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-gray-500">Recommended</dt>
                <dd className="text-gray-900 font-medium">{attraction.recomended ? 'Yes' : 'No'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-gray-500">Translation status</dt>
                <dd>
                  <Chip
                    size="sm"
                    variant="flat"
                    color={
                      attraction.translationStatus === 'success'
                        ? 'success'
                        : attraction.translationStatus === 'error'
                          ? 'danger'
                          : 'warning'
                    }
                  >
                    {attraction.translationStatus || '—'}
                  </Chip>
                </dd>
              </div>
              {attraction.languages && attraction.languages.length > 0 && (
                <div className="flex justify-between items-center gap-3">
                  <dt className="text-gray-500">Languages</dt>
                  <dd className="flex items-center gap-1">
                    {attraction.languages.map((lang) => (
                      <LangFlag key={lang} lang={lang} size={14} />
                    ))}
                  </dd>
                </div>
              )}
            </dl>
          </ActionBlock>
        </div>
      </div>
    </PageWrapper>
  );
};
